import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Breadcrumb from './components/Breadcrumb';
import CompactOverview from './components/CompactOverview';
import Alerts from './components/Alerts';
import Metrics from './components/Metrics';
import Logs from './components/Logs';
import Pipelines from './components/Pipelines';
import WebServices from './components/WebServices';
import ExternalSources from './components/ExternalSources';
import DebuggingAssistant from './components/DebuggingAssistant';
import Notifications from './components/Notifications';
import GuidedTour, { TourTrigger } from './components/GuidedTour';
import Login from './components/Login';
import AIModal from './components/AIModal';
import './App.css';

function MainApp({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTourActive, setIsTourActive] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [username, setUsername] = useState('');
  const [showAIModal, setShowAIModal] = useState(false);

  useEffect(() => {
    // Get username from localStorage
    const storedUsername = localStorage.getItem('spheroid_username') || 'User';
    setUsername(storedUsername);
  }, []);

  // Get current tab from URL path
  const getCurrentTab = () => {
    const path = location.pathname.replace('/', '');
    return path || 'overview';
  };

  useEffect(() => {
    // Check if this is a first-time user
    const hasVisited = localStorage.getItem('spheroid_visited');
    if (!hasVisited && location.pathname === '/overview') {
      setShowWelcome(true);
      localStorage.setItem('spheroid_visited', 'true');
    }
  }, [location.pathname]);

  const startTour = () => {
    navigate('/overview');
    setIsTourActive(true);
    setShowWelcome(false);
  };

  const handleTourComplete = () => {
    localStorage.setItem('spheroid_tour_completed', 'true');
  };

  const handleNavigateToLogs = (filter) => {
    navigate('/logs', { state: { filter } });
  };

  const handleAIAssistantClick = () => {
    setShowAIModal(true);
  };

  return (
    <div className="App">
      <Sidebar activeTab={getCurrentTab()} onAIAssistantClick={handleAIAssistantClick} />
      <div className="main-content">
        <Breadcrumb activeTab={getCurrentTab()} />
        <div className="user-header">
          <span className="user-greeting">Hello, {username}</span>
          <button className="logout-btn" onClick={onLogout}>Logout</button>
        </div>
        {showWelcome && (
          <div className="welcome-banner">
            <div className="welcome-content">
              <h3>Welcome to SPHEROID!</h3>
              <p>Your comprehensive Earth Observation monitoring platform. Take a guided tour to get familiar with all features.</p>
            </div>
            <div className="welcome-actions">
              <button
                className="welcome-btn"
                onClick={() => setShowWelcome(false)}
              >
                Maybe Later
              </button>
              <button
                className="welcome-btn primary"
                onClick={startTour}
              >
                Start Tour
              </button>
            </div>
          </div>
        )}
        <Routes>
          <Route path="/overview" element={<CompactOverview onNavigateToLogs={handleNavigateToLogs} />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/metrics" element={<Metrics />} />
          <Route path="/logs" element={<Logs initialFilter={location.state?.filter || 'all'} />} />
          <Route path="/pipelines" element={<Pipelines />} />
          <Route path="/web-services" element={<WebServices />} />
          <Route path="/external-sources" element={<ExternalSources />} />
          <Route path="/debugging" element={<DebuggingAssistant />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/" element={<Navigate to="/overview" replace />} />
        </Routes>
      </div>

      {!isTourActive && (
        <TourTrigger onStartTour={startTour} />
      )}

      <GuidedTour
        isActive={isTourActive}
        onClose={() => setIsTourActive(false)}
        onComplete={handleTourComplete}
      />

      <AIModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
      />
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const authStatus = localStorage.getItem('spheroid_auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (username) => {
    localStorage.setItem('spheroid_auth', 'true');
    localStorage.setItem('spheroid_username', username);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('spheroid_auth');
    localStorage.removeItem('spheroid_username');
    setIsAuthenticated(false);
  };

  return (
    <Router>
      {isAuthenticated ? <MainApp onLogout={handleLogout} /> : <Login onLogin={handleLogin} />}
    </Router>
  );
}

export default App;
